/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { ActivityIndicator, StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import type { ArcaEnvironment } from '../../../domain/fiscalTypes';
import { NumericTextInput } from '../../../../shared/ui/NumericTextInput';
import { StatusPill } from '../../../../shared/surface-cards/StatusPill';
import { settingsStyles } from '../../../../settings/screens/settingsScreen.styles';
import { spacing } from '../../../../shared/theme/spacing';
import { typography } from '../../../../shared/theme/typography';
import { ticketColors } from '../../../../shared/theme/ticketColors';

export type ArcaSetupConnectStepProps = {
  isWide: boolean;
  disabled: boolean;
  environment: ArcaEnvironment;
  certMode: 'generate' | 'manual';
  onToggleCertMode: () => void;
  certPem: string;
  onChangeCert: (value: string) => void;
  certInputRef: React.RefObject<TextInput | null>;
  privateKeyPem: string;
  onChangePrivateKey: (value: string) => void;
  privateKeyInputRef: React.RefObject<TextInput | null>;
  csrBusy: boolean;
  csrReady: boolean;
  onDownloadCsr: () => void;
  pointOfSale: string;
  onChangePointOfSale: (value: string) => void;
  showOtherBranchesHint: boolean;
  connected: boolean;
  lastInvoiceNumber?: number;
  padronWarning?: string | null;
  error: string | null;
};

export function ArcaSetupConnectStep({
  isWide,
  disabled,
  environment,
  certMode,
  onToggleCertMode,
  certPem,
  onChangeCert,
  certInputRef,
  privateKeyPem,
  onChangePrivateKey,
  privateKeyInputRef,
  csrBusy,
  csrReady,
  onDownloadCsr,
  pointOfSale,
  onChangePointOfSale,
  showOtherBranchesHint,
  connected,
  lastInvoiceNumber,
  padronWarning,
  error,
}: ArcaSetupConnectStepProps) {
  const instruction =
    environment === 'produccion'
      ? 'Descargá la solicitud, subila en Administración de Certificados Digitales y pegá el certificado.'
      : 'Descargá la solicitud, subila en el portal de prueba de ARCA y pegá el certificado.';

  return (
    <View style={styles.card}>
      <Text style={styles.title}>Conectar con ARCA</Text>
      <Text style={styles.helper}>{instruction}</Text>

      {certMode === 'generate' ? (
        <TouchableOpacity
          style={[
            settingsStyles.primaryButton,
            styles.download,
            isWide && styles.downloadWide,
            (disabled || csrBusy) && styles.disabled,
          ]}
          onPress={onDownloadCsr}
          disabled={disabled || csrBusy}
          accessibilityRole="button"
          testID="fiscal.wizard.csr-download"
        >
          {csrBusy ? (
            <ActivityIndicator color={ticketColors.goldInk} />
          ) : (
            <>
              <Ionicons name="download-outline" size={18} color={ticketColors.goldInk} />
              <Text style={settingsStyles.primaryButtonText}>Descargar solicitud</Text>
            </>
          )}
        </TouchableOpacity>
      ) : null}

      {csrReady && certMode === 'generate' ? (
        <View style={styles.success} accessibilityLiveRegion="polite" testID="fiscal.wizard.csr-ready">
          <StatusPill label="Solicitud lista" tone="success" iconName="checkmark-circle" compact />
          <Text style={styles.meta}>Subila en ARCA y pegá abajo el certificado.</Text>
        </View>
      ) : null}

      <View style={styles.field}>
        <Text style={styles.label}>Certificado</Text>
        <TextInput
          ref={certInputRef}
          accessibilityLabel="Certificado ARCA"
          style={[styles.input, styles.multiline]}
          value={certPem}
          onChangeText={onChangeCert}
          placeholder="Pegalo acá — empieza con -----BEGIN CERTIFICATE-----"
          placeholderTextColor={ticketColors.ink3}
          multiline
          autoCapitalize="none"
          editable={!disabled}
        />
      </View>

      {certMode === 'manual' ? (
        <View style={styles.field}>
          <Text style={styles.label}>Clave privada</Text>
          <TextInput
            ref={privateKeyInputRef}
            accessibilityLabel="Clave privada ARCA"
            style={[styles.input, styles.multiline]}
            value={privateKeyPem}
            onChangeText={onChangePrivateKey}
            placeholder="-----BEGIN PRIVATE KEY-----"
            placeholderTextColor={ticketColors.ink3}
            multiline
            autoCapitalize="none"
            editable={!disabled}
          />
        </View>
      ) : null}

      <TouchableOpacity
        onPress={onToggleCertMode}
        disabled={disabled}
        accessibilityRole="button"
        testID="fiscal.wizard.cert-mode-toggle"
      >
        <Text style={settingsStyles.linkText}>
          {certMode === 'generate' ? 'Ya me lo dio el contador' : 'La genero yo'}
        </Text>
      </TouchableOpacity>

      <Text style={styles.notice}>
        En Clave Fiscal, Administrador de Relaciones, delegá Facturación electrónica y Constancia de Inscripción. Lo segundo es para buscar el nombre por CUIT.
      </Text>

      <View style={styles.field}>
        <Text style={styles.label}>Punto de venta</Text>
        <NumericTextInput
          kind="integer"
          accessibilityLabel="Punto de venta"
          testID="fiscal.wizard.point-of-sale"
          style={styles.input}
          value={pointOfSale}
          onChangeText={onChangePointOfSale}
          placeholder="1"
          placeholderTextColor={ticketColors.ink3}
          editable={!disabled}
        />
        {showOtherBranchesHint ? (
          <Text style={styles.meta}>Si tenés otros locales, los configurás después.</Text>
        ) : null}
      </View>

      {connected ? (
        <View style={styles.success} accessibilityLiveRegion="polite">
          <StatusPill label="Conectado" tone="success" iconName="checkmark-circle" />
          <Text style={styles.meta}>
            {environment === 'produccion'
              ? 'Ya podés facturar.'
              : 'Listo para probar. Cuando quieras facturar de verdad, cambialo en el primer paso.'}
          </Text>
          {lastInvoiceNumber != null ? (
            <Text style={styles.meta}>Último comprobante: {lastInvoiceNumber}</Text>
          ) : null}
        </View>
      ) : null}

      {connected && padronWarning ? (
        <View style={styles.errorBox} accessibilityLiveRegion="polite" testID="fiscal.wizard.padron-warning">
          <StatusPill label="Falta el padrón" tone="warning" iconName="alert-circle-outline" compact />
          <Text style={styles.meta}>{padronWarning}</Text>
        </View>
      ) : null}

      {error ? (
        <View style={styles.errorBox} accessibilityLiveRegion="polite">
          <StatusPill label="No se pudo conectar" tone="danger" iconName="alert-circle-outline" compact />
          <Text style={styles.meta}>{error}</Text>
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: ticketColors.paper,
    borderRadius: 14,
    padding: spacing.lg,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    gap: spacing.md,
  },
  title: { ...typography.titleLg, color: ticketColors.ink },
  helper: { ...typography.body, color: ticketColors.ink2, lineHeight: 22 },
  download: { flexDirection: 'row', gap: spacing.sm, minHeight: 52 },
  downloadWide: { alignSelf: 'flex-start', minWidth: 260, paddingHorizontal: spacing.xl },
  field: { gap: spacing.sm },
  label: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '700',
  },
  input: {
    backgroundColor: ticketColors.paper2,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    color: ticketColors.ink,
    minHeight: 52,
  },
  multiline: { minHeight: 120, textAlignVertical: 'top' },
  notice: { ...typography.caption, color: ticketColors.ink2, lineHeight: 18 },
  meta: { ...typography.caption, color: ticketColors.ink2, lineHeight: 18 },
  success: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(61, 214, 140, 0.28)',
    backgroundColor: 'rgba(61, 214, 140, 0.06)',
    padding: spacing.md,
    gap: spacing.sm,
  },
  errorBox: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(255, 107, 107, 0.35)',
    backgroundColor: 'rgba(255, 107, 107, 0.06)',
    padding: spacing.md,
    gap: spacing.sm,
  },
  disabled: { opacity: 0.5 },
});
